import { logError } from 'App/services/coralogixService';
import { analyticsTrackDynamicScreenerStart } from 'Shared/services/analytics/events/dynamicScreenerStart';
import { getDynamicScreenerTestData } from 'Shared/services/opa/app/dynamicScreenerSplitTest';
import { getUserCacheData, setUserCacheData, UserSessionCacheKeys } from 'Shared/services/sessionCacheService';
import { getDynamicScreeningQuestions } from 'Shared/services/usp/api/uspApiService';
import { UspSurveysModel } from 'Shared/services/usp/models/UspSurveysModel';
import { USPAnswerType,UspUnansweredQuestions } from 'Shared/services/usp/models/UspUnansweredQuestionsModel';

import { DYNAMIC_SCREENER_QUESTION_KEY } from './questionService';

export interface DynamicScreenerQuestion {
  id         : string;
  question   : string;
  displayMode: {
    type: 'SINGLE_CHOICE' | 'MULTI_CHOICE' | 'OPEN_ENDED';
    inputType?: 'TEXT' | 'NUMBER' | 'UNKNOWN' | 'UNDEFINED';
    constraints?: {
      constraintType?: 'NUMBER' | 'TEXT';
      min?: number;
      max?: number;
    };
  };
  answers: Array<{
    id   : string;
    label: string;
  }>;
}

export interface DynamicScreenerCache {
  updatedAt: number;
  questions: {
    [key: string]: DynamicScreenerQuestion[];
  };
}

/**
 * cache for ongoing API requests
 */
const memoryCache = new Map<string, Promise<DynamicScreenerQuestion[]>>();

/**
 * Get the dynamic screener cache
 * @returns the dynamic screener cache
 */
const getDynamicScreenerCache = (): DynamicScreenerCache => {
  return (
    getUserCacheData(UserSessionCacheKeys.DYNAMIC_SCREENER_QUESTIONS) ?? {
      updatedAt: Date.now(),
      questions: {},
    }
  ) as DynamicScreenerCache;
};

/**
 * Save the dynamic screener cache to local storage
 * @param cache the dynamic screener cache
 */
const setDynamicScreenerCache = (cache: DynamicScreenerCache): void => {
  cache.updatedAt = Date.now();
  setUserCacheData(UserSessionCacheKeys.DYNAMIC_SCREENER_QUESTIONS, cache);
};

/**
 * Generate a cache key
 * @param providerId the provider ID
 * @param questionIds the question IDs
 * @returns the cache key
 */
const createCacheKey = (providerId: number, questionIds: string[]): string => {
  return `${providerId}:${questionIds.join(',')}`;
};

/**
 * Fetch and cache dynamic screener cache questions
 * @param providerId the provider ID
 * @param questionIds the question IDs
 * @param cache the dynamic screener cache
 */
export const fetchDynamicQuestions = async (providerId: number, questionIds: string[],  cache: DynamicScreenerCache): Promise<void> => {
  if (!questionIds.length) {
    return;
  }

  const key     = createCacheKey(providerId, questionIds);
  let   promise = memoryCache.get(key);

  if (!promise) {
    promise = getDynamicScreeningQuestions(providerId, questionIds)
      .then((response) => response.data.questions as DynamicScreenerQuestion[])
      .catch((error) => {
        memoryCache.delete(key);
        logError(`Error fetching dynamic screener cache questions for provider ${providerId}:`, error);
        return [] as DynamicScreenerQuestion[];
      });
    memoryCache.set(key, promise);
  }

  try {
    const questions = await promise;
    const providerKey = providerId.toString();
    cache.questions[providerKey] = [...(cache.questions[providerKey] || []), ...questions];
    setDynamicScreenerCache(cache);
  } catch (error) {
    logError(`Error fetching dynamic screener cache questions for provider ${providerId}:`, error);
  }
};

/**
 * Get the cached questions for a provider
 * @param providerId the provider ID
 * @param cache the dynamic screener cache
 * @returns the cached questions
 */
export const getProviderCachedQuestions = (providerId: number, cache: DynamicScreenerCache): DynamicScreenerQuestion[] => {
  return cache.questions[providerId.toString()] || [];
};

interface GroupedUnansweredDynamicQuestions {
  [providerId: number]: string[];
}

/**
 * Group the unanswered dynamic questions by provider
 * @param surveyList the survey list
 * @param allowedProviders the allowed providers
 * @returns the grouped unanswered dynamic questions
 */
export const groupUnansweredDynamicQuestions = (surveyList: UspSurveysModel[], allowedProviders: number[]): GroupedUnansweredDynamicQuestions => {
  const grouped: { [providerId: number]: Set<string> } = {};

  surveyList.forEach((survey) => {
    if (!allowedProviders.includes(survey.providerId)) {
      return;
    }

    grouped[survey.providerId] = grouped[survey.providerId] || new Set();
    survey.unansweredDynamicQuestionIds?.forEach((qId) => grouped[survey.providerId].add(qId));
  });

  return Object.fromEntries(
    Object.entries(grouped).map(([providerId, questions]) => [providerId, Array.from(questions)])
  );
};

/**
 * Process the dynamic questions
 * @param surveyList the survey list
 */
export const processDynamicQuestions = async (surveyList: UspSurveysModel[]): Promise<void> => {
  const splitTest = getDynamicScreenerTestData();
  if (!splitTest.enabled || !splitTest.providerIds.length) {
    return;
  }

  const cache                    = getDynamicScreenerCache();
  const latestDynamicQuestionIds = groupUnansweredDynamicQuestions(surveyList, splitTest.providerIds);

  const updatePromises = Object.entries(latestDynamicQuestionIds).map(async ([providerId, questionIds]) => {
    const providerNum        = Number(providerId);
    const cachedQuestions    = getProviderCachedQuestions(providerNum, cache);
    const missingQuestionIds = questionIds.filter(
      (qId: string) => !cachedQuestions.some((q) => q.id === qId)
    );
    await fetchDynamicQuestions(providerNum, missingQuestionIds, cache);
  });

  await Promise.all(updatePromises);
};

/**
 * Get the answer type for a dynamic screener question
 * @param question
 * @returns
 */
const getAnswerType = (question: DynamicScreenerQuestion): USPAnswerType => {
  switch (question.displayMode.inputType) {
    case 'TEXT':
      return USPAnswerType.TEXT;
    case 'NUMBER':
      return USPAnswerType.NUMBER;
    default:
      return USPAnswerType.TEXT;
  }
};

/**
 * Maps dynamic screener questions to USP format
 */
export const mapDynamicScreenerQuestions = (questions: DynamicScreenerQuestion[], providerId: number): UspUnansweredQuestions[] => {
  return questions
    // .filter(q => q.answers && q.answers.length > 0 && q.displayMode.type.toUpperCase() !== 'OPEN_ENDED')
    .map(question => ({
      id                  : Number(question.id),
      inputTypeId         : 1,
      key                 : `${DYNAMIC_SCREENER_QUESTION_KEY}${question.id}`,
      question            : question.question,
      answerType          : question.displayMode.type.toUpperCase() !== 'OPEN_ENDED' ? USPAnswerType.TEXT : getAnswerType(question),
      multiValue          : question.displayMode.type.toUpperCase() === 'MULTI_CHOICE',
      hasPredefinedAnswers: question.displayMode.type.toUpperCase() !== 'OPEN_ENDED',
      enabled             : true,
      sortOrder           : 100,
      predefinedAnswers   : question.answers && question.answers.length > 0 ? question.answers.map(answer => ({
        id         : Number(answer.id),
        questionId : Number(question.id),
        label      : answer.label,
        sortOrder  : Number(answer.id),
        enabled    : true,
        answerValue: Number(answer.id),
        solo       : false
      })) : [],
      //for dynamic screener questions, im using categoryId to store the providerID which is later needed to save the answers
      categoryId  : Number(`${providerId}`),
      required    : true,
      typeId      : 0,
      categoryName: '',
      criteriaKey : undefined,
      constraints : question.displayMode.constraints
    }));
};

/**
 * Maps dynamic screener questions to unanswered questions format
 * @param survey
 * @returns
 */
export const mapDynamicScreenerQuestionsToUnansweredQuestions = (survey: UspSurveysModel): UspUnansweredQuestions[] => {

  //exit early if the test is disabled, no provider IDs are set,
  //or the survey has no unansweredDynamicQuestionIds
  if (!survey.unansweredDynamicQuestionIds || survey.unansweredDynamicQuestionIds.length === 0) {
    return [];
  }

  //retrieve dynamic screener questions for this provider
  const dynamicQuestions = getUserCacheData(UserSessionCacheKeys.DYNAMIC_SCREENER_QUESTIONS)?.questions[survey.providerId] || [];

  //filter only the questions that are still unanswered
  const filteredDynamicQuestions = dynamicQuestions.filter(question =>
    survey.unansweredDynamicQuestionIds.includes(question.id)
  );

  if (filteredDynamicQuestions.length === 0) {
    return [];
  }

  //if there are unanswered questions, track the start event
  analyticsTrackDynamicScreenerStart(survey.id, survey.providerId.toString(), filteredDynamicQuestions.length);

  //map questions to UPM/USP format needed for the questionnaire popup
  return mapDynamicScreenerQuestions(filteredDynamicQuestions, survey.providerId);
};
